import { Injectable } from '@angular/core';
import { HttpMethodType } from '@app/app.constant';
import {
    AddFamilyStaff,
    IAddFamilyMemberRequest,
    IFamilyDetailInfoRequest,
    IFamilyDetailResponseHttp,
    IFamilyDetailSaveRequestHttp,
    IGetAddFamilyMemberDetailInfoResponse,
    IPotantialContactRequest,
    IPotantialStaffRequest,
    FamilyInfo
} from '@app/models/family/family-detail.model';
import { IGetFamilyHeaderByProjectId, IFamilyGridInfoHttp } from '@app/models/family/family-grid.model';
import { HttpCallService } from '@core/services/http-call.service';
import { Observable, map } from 'rxjs';

const HttpServiceMethods = {
    Family: 'Family',
    // Route was declared 'Family/GetFamilyHeaderByProjectId' since creation — the controller
    // route is GetFamiliesHeaderByProjectId. Dead code until the family filters used it, so
    // the 404 never surfaced.
    GetFamilyHeaderByProjectId: 'Family/GetFamiliesHeaderByProjectId',
    GetFamilyGridInfoByProjectId: 'Family/GetFamilyGridInfoByProjectId',
    GetFamilyDetailInfoByGuid: 'Family/GetFamilyDetailInfoByGuid',
    SaveFamilyDetailInfo: 'Family/SaveFamilyDetailInfo',
    GetFamilyPotentialStaffs: 'FamilyStaff/GetFamilyPotentialStaffs',
    AddFamilyStaff: 'FamilyStaff/AddFamilyStaff',
    GetFamilyStaffs: 'FamilyStaff/GetFamilyStaffs',
    DeleteFamilyStaff: 'FamilyStaff/DeleteFamilyStaff',
    GetFamilyPotentialContacts: 'FamilyContact/GetFamilyPotentialContacts',
    GetFamilyContacts: 'FamilyContact/GetFamilyContacts',
    DeleteFamilyContact: 'FamilyContact/DeleteFamilyContact',
    GetFamiliesByContactGuid: 'FamilyContact/GetFamiliesByContactGuid',
    GetFamilyContactPotentialFamilies: 'FamilyContact/GetFamilyContactPotentialFamilies',
    GetAddFamilyMemberDetailInfo: 'FamilyContact/GetAddFamilyMemberDetailInfo',
    AddFamilyMember: 'FamilyContact/AddFamilyMember'
};

@Injectable({
    providedIn: 'root'
})
export class FamilyService {

    constructor(private httpService: HttpCallService) { }

    public getFamilysHeaderByProjectId(): Observable<IGetFamilyHeaderByProjectId[]> {
        return this.httpService.callService(HttpMethodType.GET, HttpServiceMethods.GetFamilyHeaderByProjectId);
    }

    public getFamilyGridInfoByProjectId(): Observable<IFamilyGridInfoHttp> {
        return this.httpService.callService(
            HttpMethodType.GET,
            HttpServiceMethods.GetFamilyGridInfoByProjectId,
        );
    }

    public getFamilyDetailInfoByGuid(request: IFamilyDetailInfoRequest): Observable<FamilyInfo> {
        return this.httpService.callService(
            HttpMethodType.GET,
            HttpServiceMethods.GetFamilyDetailInfoByGuid + `/${request.familyGuid}`,
        ).pipe(map((res: IFamilyDetailResponseHttp) => {
            return FamilyInfo.fromJson(res);
        }));
    }

    public saveFamilyDetailInfo(body: IFamilyDetailSaveRequestHttp): Observable<string> {
        return this.httpService.callService(HttpMethodType.POST, HttpServiceMethods.SaveFamilyDetailInfo, body);
    }

    public deleteFamily(FamilyGuid: string): Observable<string> {
        return this.httpService.callService(HttpMethodType.DELETE, HttpServiceMethods.Family + `/${FamilyGuid}`);
    }

    public getPotentialStaffs(request: IPotantialStaffRequest) {
        return this.httpService.callService(
            HttpMethodType.POST,
            HttpServiceMethods.GetFamilyPotentialStaffs, request
        );
    }

    public addFamilyStaff(FamilyGuid: string, staffGuid: string): Observable<AddFamilyStaff[]> {
        return this.httpService.callService(HttpMethodType.POST, HttpServiceMethods.AddFamilyStaff + `/${FamilyGuid}` + `/${staffGuid}`);
    }

    public deleteFamilyStaff(FamilyGuid: string, staffGuid: string): Observable<string> {
        return this.httpService.callService(HttpMethodType.DELETE, HttpServiceMethods.DeleteFamilyStaff + `/${FamilyGuid}` + `/${staffGuid}`);
    }

    public getPotentialContacts(request: IPotantialContactRequest) {
        return this.httpService.callService(
            HttpMethodType.POST,
            HttpServiceMethods.GetFamilyPotentialContacts, request
        );
    }

    public deleteFamilyContact(familyGuid: string, contactGuid: string): Observable<string> {
        return this.httpService.callService(HttpMethodType.DELETE, HttpServiceMethods.DeleteFamilyContact + `/${familyGuid}` + `/${contactGuid}`);
    }

    public getFamiliesByContactGuid(contactGuid: string) {
        return this.httpService.callService(
            HttpMethodType.GET,
            `${HttpServiceMethods.GetFamiliesByContactGuid}/${contactGuid}`
        );
    }

    /** A family's members — used by the family filters (add-to-activity, attendance) to
     *  narrow contact lists to one family's guids. Response rows carry the member under
     *  `contact` (full contact document); callers should read `contact.contactGuid`. */
    public getFamilyContactsByFamilyGuid(familyGuid: string): Observable<any[]> {
        return this.httpService.callService(
            HttpMethodType.GET,
            `${HttpServiceMethods.GetFamilyContacts}/${familyGuid}`
        );
    }

    public getFamilyContactPotentialFamilies(contactGuid: string) {
        return this.httpService.callService(
            HttpMethodType.GET,
            `${HttpServiceMethods.GetFamilyContactPotentialFamilies}/${contactGuid}`
        );
    }

    public getAddFamilyMemberDetailInfo(): Observable<IGetAddFamilyMemberDetailInfoResponse> {
        return this.httpService.callService(
            HttpMethodType.GET,
            HttpServiceMethods.GetAddFamilyMemberDetailInfo
        );
    }

    public addFamilyMember(request: IAddFamilyMemberRequest): Observable<string> {
        return this.httpService.callService(
            HttpMethodType.POST,
            HttpServiceMethods.AddFamilyMember,
            request
        );
    }
}
